import React, { Fragment, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import toast from 'react-hot-toast'

import styles from './Timezone.module.css'

import FormControl from 'Design/components/FormControl'
import Label from 'Design/components/Label'
import HelpText from 'Design/components/HelpText'
import Button from 'Design/components/Button'
import FormErrorText from 'Design/components/FormErrorText'
import PageLoader from 'Layout/PageLoader'
import TimezonePicker from 'Components/TimezonePicker'

import { useUpdateBotProfileMutation, useGetBotProfileQuery } from 'Services/bot-profiles'
import { useGetAccountQuery } from 'Services/account'

import timezoneFormValidationSchema from './timezone-form-validation-schema'

const Timezone = ({ account = {}, bot = {} } = {}) => {
  const { id: botProfileId, settings: botSettings = {} } = bot
  const { settings: accountSettings = {} } = account
  const { timezone: botProfileTimezone } = botSettings
  const { timezone: accountTimezone } = accountSettings

  const timezone = useMemo(() => {
    return botProfileTimezone || accountTimezone
  }, [botProfileTimezone, accountTimezone])

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors, isDirty, isSubmitSuccessful },
    reset,
    getValues
  } = useForm({
    defaultValues: {
      timezone
    },
    resolver: yupResolver(timezoneFormValidationSchema)
  })

  const [updateBotProfile, botProfileResult] = useUpdateBotProfileMutation()

  const onFormSubmit = (data) => {
    const payload = {
      id: botProfileId,
      settings: {
        ...botSettings,
        timezone: data.timezone
      }
    }

    const promise = updateBotProfile(payload).unwrap()

    toast.promise(promise, {
      loading: 'Updating timezone settings',
      success: 'Timezone settings updated successfully',
      error: 'Error while updating timezone settings. Try again...'
    })

    return promise
  }

  useEffect(() => {
    if (!isSubmitSuccessful) {
      return
    }

    reset({ ...getValues() })
  }, [isSubmitSuccessful, reset, getValues])

  const disableSubmitButton = isSubmitting || botProfileResult.loading || !isDirty

  return (
    <Fragment>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <FormControl isInvalid={errors.timezone}>
          <Label>Timezone</Label>
          <Controller
            control={control}
            name='timezone'
            render={({ field: { value, onChange } }) => {
              return (
                <TimezonePicker
                  placeholder='Select timezone'
                  defaultValue={{ value }}
                  onChange={(option) => onChange(option?.value)}
                  className={styles.TimezonePicker}
                />
              )
            }}
          />
          <HelpText>Time stamps in bot transcripts will use this timezone</HelpText>
          {errors.timezone && <FormErrorText>{errors.timezone.message}</FormErrorText>}
        </FormControl>

        <Button type='submit' variant='primary' disabled={disableSubmitButton}>
          Save
        </Button>
      </form>
    </Fragment>
  )
}

const TimezoneContainer = ({ botId }) => {
  const { data: account = {}, isLoading: isAccountLoading } = useGetAccountQuery(null)
  const { data: bot = {}, isLoading: isBotLoading } = useGetBotProfileQuery(botId)

  if (isAccountLoading || isBotLoading) {
    return <PageLoader />
  }

  return <Timezone account={account} bot={bot} />
}

export default TimezoneContainer
